import { Component, Input } from '@angular/core';
import { IParamsField, IProductCardForm, IProductCardPackingForm, ProductTypes } from '../../../product/models/models';
import { PHPermissions } from '../../models/PHPermissions';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ProductCard } from '../../../product/models/product-card';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { UnitConverter } from '../../utils/unitConverter';
import { GlobalSettings } from '../../models/globalSettings';
import { Utils } from '../../services/utils.service';
import { SessionService } from '../../services/session.service';

import { SHARED_MODULES } from '../../../shared.';
import { HasAccessPipe } from '../../pipes/has-access.pipe';
import { MeasurementUnitPipe } from '../../pipes/measurement-unit.pipe';
import { ImageSelectComponent } from '../image-select/image-select.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';

@Component({
    selector: 'app-product-packing-edit',
    templateUrl: './product-packing-edit.component.html',
    styleUrls: ['./product-packing-edit.component.scss'],
	imports: [
		SHARED_MODULES,
		HasAccessPipe,
		MeasurementUnitPipe,
		ImageSelectComponent,
		PhButtonComponent,
		PhIconComponent
	],
    standalone: true
})
export class ProductPackingEditComponent {

	public fields: Array<IParamsField>;
	public readonly permissions: typeof PHPermissions = PHPermissions;
	public readonly productTypes: typeof ProductTypes = ProductTypes;

	@Input() product: ProductCard;
	@Input() productForm: FormGroup<IProductCardForm>;
	@Input() showErrors: boolean = false;

	constructor(private sessionService: SessionService) {}

	public onDelete(index: number): void {
		const packing: FormArray<FormGroup<IProductCardPackingForm>> = this.productForm.controls.packing;
		packing.markAsDirty();
		packing.removeAt(index);
	}

	public onChangeImage(photo: string, index: number): void {
		this.productForm.controls.packing.controls[index].controls.photo.markAsDirty();
		this.productForm.controls.packing.controls[index].patchValue({ photo: photo });
	}

	public onCreate(): void {
		const isLoose: boolean = this.productForm.controls.is_loose.value;
		this.productForm.controls.packing.markAsDirty();
		this.productForm.controls.packing.push(
			new FormGroup<IProductCardPackingForm>({
				id: new FormControl<number>(null),
				name: new FormControl<string>(null, [
					Validators.maxLength(100),
					Validators.required,
					RxwebValidators.unique()
				]),
				qty: new FormControl<number>({ value: null, disabled: isLoose }, [
					Validators.min(1),
					Validators.pattern('\\d+'),
					Validators.required
				]),
				weight: new FormControl<number>({ value: null, disabled: !isLoose }, [
					Validators.min(UnitConverter.getConvertValue(0.001, 'mass')),
					Validators.pattern('(\\d+\\.?)+'),
					Validators.required
				]),
				height: new FormControl<number>(
					this.productForm.controls.height.value || UnitConverter.getConvertValue(this.product.params.height),
					[
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+'),
						Validators.required
					]
				),
				depth: new FormControl<number>(
					this.productForm.controls.depth.value || UnitConverter.getConvertValue(this.product.params.depth),
					[
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+'),
						Validators.required
					]
				),
				width: new FormControl<number>(
					this.productForm.controls.width.value || UnitConverter.getConvertValue(this.product.params.width),
					[
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+'),
						Validators.required
					]
				),
				photo: new FormControl<string>(this.productForm.controls.photo.value || this.product.params.photo),
				color: new FormControl<string>(Utils.getRandomColor()),
				position: new FormControl<number>(null)
			})
		);
	}

	public numberInputValidate(event: KeyboardEvent, validDot: boolean = true): boolean {
		const blockedSymbols: string[] = ['e', 'E', '-', '+'];
		if (!validDot) blockedSymbols.push('.', ',');
		return !blockedSymbols.includes(event.key);
	}

	public wheelInput(event: WheelEvent): void {
		(event.target as HTMLElement).blur();
	}
}
